// Central tunables. Keeping every magic number here means balance passes
// (M8) touch one file instead of hunting through system code.

// Low-res virtual backbuffer — rendered at this resolution, then scaled up
// to fill the screen with nearest-neighbor sampling for a crisp pixel-art look.
export const VIRTUAL_WIDTH = 384;
export const VIRTUAL_HEIGHT = 216;

// Canvas-side HUD text uses the same face the DOM loads via @font-face
// (src/style.css --font-ui) so overlay text and in-run text match. Canvas
// falls back through the same stack until the woff2 finishes loading.
export const UI_FONT = "'Space Mono', 'Courier New', monospace";

// Rectangular arena the fight takes place in (world-space units == virtual
// px). Larger than one screen — the camera follows the player (Camera.js)
// rather than showing the whole arena at once.
export const ARENA = {
  x: 0,
  y: 0,
  width: 700,
  height: 500,
};

export const PLAYER = {
  radius: 4,
  moveSpeed: 72, // px/sec (60 * 1.2)
  maxHp: 100,

  dashRange: 35.2, // px traveled per dash (was tracked as 220px/sec * 0.16s duration; speed is derived below)
  dashDuration: 0.16, // seconds
  dashCooldown: 2.0, // seconds, per the spec
  dashInvincibleDuration: 0.2, // i-frame window, covers the full dash burst

  // Post-hit mercy window (Player.takeDamage) — the main survivability knob.
  // Every damage source resolves independently: a wave is 8-14 minions that
  // each carry their own 0.5s contact cooldown, and boss beams/zones tick on
  // top, so without a shared player-side window they all landed in the SAME
  // tick and a full 100 HP bar could vanish faster than a dash could answer.
  // This caps incoming damage at one hit per window instead of N.
  //
  // 0.4s is a deliberate middle: long enough to fully collapse same-tick
  // swarm burst (~144 DPS surrounded -> ~30), short enough that sustained
  // hazards keep their bite rather than every threat flattening to one
  // shared DPS ceiling (beam ticks run 0.1-0.2s, so they still out-pressure
  // contact damage). Raise it if runs still feel swingy; lower it if beams
  // and damage zones start feeling tankable.
  hitInvulnDuration: 0.4, // seconds
};

// Focus mode (BULLET-HELL.md §D4) — hold the focus key to trade mobility for
// precision, and to see the hull and graze band you're actually threading.
//
// The genre's version of this key also tightens your shot spread; Survivor's
// weapons auto-aim, so there's nothing there to trade. The cost is purely
// speed, which is the right one: it makes focus a commitment inside a
// pattern rather than a state you'd hold all run.
//
// Dash is deliberately exempt (Player.update only applies the multiplier to
// the walking branch) — focus is about micro-positioning between dashes, and
// a dash that fired at 45% range would read as the key eating your escape.
export const FOCUS = {
  // 45%: slow enough that weaving a dense wall is a different act from
  // walking, fast enough that you can still reposition a few bullet-widths
  // without releasing.
  speedMultiplier: 0.45,
  // Hull dot / graze ring visuals. The dot is drawn at PLAYER.radius — it is
  // the literal collision circle, not a stylized approximation.
  //
  // The ring is drawn at PLAYER.radius + GRAZE.band + the NEAREST BULLET's
  // radius, because that is the near-miss test in
  // SpawningSystem._checkPlayerCollisions: the boundary is a function of both
  // circles, so a ring fixed at PLAYER.radius + GRAZE.band was accurate only
  // against a zero-radius bullet. See Game._updateFocusRing.
  dotColor: '#ffffff',
  ringColor: 'rgba(126, 151, 255, 0.75)',
  // The contact boundary for that same bullet (PLAYER.radius + its radius) —
  // the inner edge of the band, dimmer than the outer ring because it is the
  // line you must not cross rather than the one you are aiming to sit on.
  contactColor: 'rgba(255, 122, 138, 0.55)',
  // Seconds for the dot/ring to fade fully in or out, so tapping focus
  // doesn't pop.
  fadeDuration: 0.12,
  // How fast the ring resizes as the nearest bullet changes: ringEaseSpan px
  // per ringEaseDuration seconds. Tuned to cross the whole plausible bullet
  // range (0..~8px) in about a tenth of a second — fast enough to be showing
  // the current bullet, slow enough that a churning curtain doesn't make the
  // ring flicker.
  ringEaseDuration: 0.1,
  ringEaseSpan: 8,
};

// Render-layer readability (BULLET-HELL.md §D0). The danmaku rendering law is
// that hostile bullets and the player's hull are the top two layers, always —
// they are the only objects the player must never lose track of. Game._render
// enforces the order; this holds the one tunable that goes with it.
export const RENDER = {
  // Player projectile alpha while a hostile curtain is on screen (boss stage,
  // or the Echo Duel ghost mid-waves). A late-run build fires enough of its
  // own shots to hide a bullet behind them even when the bullets are drawn on
  // top, so the player's own fire is knocked back a quarter — the same reason
  // Cave games dim the player's shot. Never applied outside those stages: in
  // the wave half the projectiles ARE the thing worth watching.
  bossProjectileAlpha: 0.75,
};

// Graze (bullet-hell near-miss reward): a boss/minion bullet whose edge
// passes within `band` px of the player's hull without landing charges the
// ultimate meter by ULTIMATE.chargePerGraze — once per bullet (BossBullet's
// `grazed` latch, checked in SpawningSystem._checkPlayerCollisions).
// Hull contact during dash i-frames counts as a graze too, before the
// bullet is absorbed exactly as it is today.
//
// Since BULLET-HELL.md §D5 the grazes also CHAIN (systems/combat/GrazeChain.js):
// every graze refreshes a short window, and the chain length raises the value
// of the next one. The genre's rule is that risk must be legible and paid
// immediately — a graze was previously worth 0.012 of a bar and 0.15 Glimmer
// settled at the run-end screen, which is an accounting entry, not a reward.
export const GRAZE = {
  band: 6, // px beyond the hit radius that still reads as a near-miss
  // Seconds a chain survives without a new graze. ~2s is the genre standard
  // and it is the right length here for a specific reason: it is longer than
  // the gap between bullets inside one dense pattern and shorter than the gap
  // between two patterns, so a chain measures "I threaded that card", not
  // "I was in the fight".
  chainWindow: 2,
  // Multiplier = 1 + count * perGraze, capped. 0.04/graze to a 2.5x ceiling
  // means the cap sits at ~38 chained grazes — reachable inside a GridBarrage
  // or a long Undertow, and not reachable at all by drifting near one bullet
  // every second.
  multiplierPerGraze: 0.04,
  multiplierMax: 2.5,
  // Graze as the mote magnet (BULLET-HELL.md §D5's "consider"): extra magnet
  // radius, in px, at full multiplier, tapering to 0 at 1x. This is the half
  // that makes risk and harvest the same act — threading the curtain hoovers
  // the floor — and it is bounded well under a screen-wide vacuum so it
  // shortens the walk to the motes rather than deleting it.
  magnetBonusAtMax: 26,
  // The chain readout at the player. Warm gold, matching the graze sparks
  // JuiceDirector already throws and the meter the chain feeds.
  color: '#ffd166',
  // Seconds the readout lingers after a chain lapses, so the number you ended
  // on is readable instead of vanishing on the frame it expires.
  readoutFade: 0.5,
};

// Shared across every boss in the gauntlet (see data/bosses.js) — physical
// size and phase-gating stay consistent; maxHp/moveSpeed/attack tuning are
// what actually escalate boss-to-boss.
export const BOSS = {
  radius: 18,
  phaseThresholds: [0.75, 0.50, 0.25], // HP fractions that trigger new phases
};

export const WEAPONS = {
  spiritLance: {
    name: 'Spirit Lance',
    damage: 12,
    fireRate: 0.6,
    projectileSpeed: 280,
    projectileRadius: 2.5,
    color: '#7e97ff',
    lifetime: 1.5,
  },
  scatterCannon: {
    name: 'Scatter Cannon',
    damage: 6,
    fireRate: 0.9,
    projectileSpeed: 220,
    projectileRadius: 2,
    pellets: 5,
    spread: 0.6,
    color: '#f4e04d',
    lifetime: 0.8,
  },
  arcNeedle: {
    name: 'Arc Needle',
    damage: 4,
    fireRate: 0.35,
    projectileSpeed: 200,
    projectileRadius: 1.5,
    turnRate: 3.5,
    color: '#8ad1ff',
    lifetime: 2.5,
  },
  orbitWard: {
    name: 'Orbit Ward',
    damage: 4,
    projectileRadius: 4, // larger shards (was 2) — more presence, easier to graze bullets with
    orbitRadius: 34, // orbit well outside the player (was 10) so it's a real defensive ring, not a melee hug
    angularSpeed: 8, // rad/sec (was 4 — doubled spin speed)
    hitCooldownDuration: 0.25,
    blocksBullets: false, // shards no longer delete incoming boss bullets (removed defensive utility)
    knocksBack: true, // shoves minions away on hit (not the boss — see CombatSystem._checkCollisions)
    knockbackForce: 12, // px, one-shot nudge per hit tick
    color: '#c9a8ff',
  },
  novaPulse: {
    name: 'Nova Pulse',
    damage: 10,
    fireRate: 1.4,
    radius: 80, // 4× the old base radius (was 20); still scales +level*2 on top (see NovaPulse._outerRadius)
    color: '#ffd166',
  },
  // --- Act II (ACT2-BIBLE.md §7) --------------------------------------------
  // Both are ordinary data-driven weapons available from run start in every
  // mode; neither depends on reaching Act II.
  resonor: {
    name: 'Resonor',
    damage: 8,
    // Seconds between volleys — the whole interval is spent in one burst
    // (see Resonor.js), so this reads longer than the weapon feels. 1.2 is the
    // cadence the weapon actually ran at for its whole life: it used to hold a
    // ready volley for the next beat, and at the roster's middle tempo that
    // 0.75 timer alternated between ~0.75 and ~1.11s periods for a ~0.9 mean.
    // Kept as the literal fire rate so dropping the hold didn't hand a starter
    // ~60% more damage — and it puts Shatterpoint back on its 1.15x/1.5x
    // parent ratio without touching the fusion's own numbers.
    fireRate: 0.9,
    projectileSpeed: 300,
    projectileRadius: 2.5,
    color: '#c9a0ff',
    lifetime: 1.2,
    burst: 2, // shots per volley at level 1; +1 at levels 3/5/7
    subdivision: 0.11, // seconds between the shots inside one burst
    leadShotMultiplier: 1.6, // the first shot of a burst lands hardest
  },
  umbralLantern: {
    name: 'Umbral Lantern',
    // A short beam, modelled as a line of overlapping stationary segments
    // re-struck every fireRate — it goes through the same pooled-projectile
    // collision path as everything else rather than needing a beam primitive.
    damage: 5,
    fireRate: 0.22,
    range: 62, // world px from the player; short on purpose — this is arm's length
    segments: 5,
    projectileRadius: 4,
    lifetime: 0.1, // just longer than a fire interval, so the beam reads continuous
    color: '#ffe9b0',
    // The hook nothing else has: the lantern *emits light*, widening the
    // player's lit disc in a Gloaming fight (Game._drawGloaming). Free
    // everywhere else — no other fight darkens the arena.
    lightRadius: 46,
  },
  // --- Act III (ACT3-BIBLE.md §7) -------------------------------------------
  // Both are ordinary data-driven weapons available from run start in every
  // mode; neither depends on reaching Act III.
  glyphpen: {
    name: 'Glyphpen',
    // The first weapon whose output is POSITIONAL rather than directional: it
    // doesn't shoot at the target, it writes marks on the floor near the player
    // that then pulse. Modelled as short-lived stationary discs re-struck on a
    // cadence — the same "a zone is a run of stationary hits" trick the Umbral
    // Lantern uses for its beam, so it needs no new collision primitive.
    damage: 7,
    fireRate: 0.85,
    marks: 2, // marks written per cast; +1 at levels 4 and 7
    range: 52, // how far from the player a mark can land
    projectileRadius: 13,
    lifetime: 0.7, // the mark's dwell — long enough to catch something walking in
    color: '#ffd166',
  },
  // Tine's starter (BULLET-HELL.md §D7, the champion promotion). Like every
  // starter it is usable by anyone and depends on nothing about its champion.
  tuningFork: {
    name: 'Tuning Fork',
    // Two prongs, launched apart and homing back together onto the same
    // target: the shots visibly *converge*, which is the image the whole
    // champion runs on (two tones, one note). Mechanically it is a pair of
    // weak homing shots — the convergence is what it says, not what it costs.
    damage: 8,
    fireRate: 0.42,
    prongSpread: 0.7, // radians between the two prongs at launch
    turnRate: 5.5, // rad/sec each prong may steer back toward the target
    projectileRadius: 3,
    projectileSpeed: 300,
    lifetime: 1.2,
    color: '#ff6fae',
  },
  countersong: {
    name: 'Countersong',
    // A close arc that CONVERTS enemy bullets it touches into friendly shots.
    // Distinct from Orbit Ward, which used to simply delete them: this gives
    // them back, and it is the first reflect in the game. The conversion itself
    // is a cross-pool interaction, so it lives where both pools are visible
    // (Game._convertBossBullets), and this config is what it reads.
    damage: 9,
    fireRate: 0.5,
    arcRadius: 46, // how far the arc reaches from the player
    arcWidth: 1.5, // radians of the sweep, centred on facing
    projectileRadius: 3,
    projectileSpeed: 260,
    lifetime: 1.1,
    color: '#66d9ff',
    reflectDamage: 26, // damage a converted bullet carries, before level scaling
    reflectDamagePerLevel: 3.4,
    reflectSpeed: 300,
    // One bullet turned around at a time, on this cadence. The arc is held
    // continuously (see Countersong.update), so without this a dense wall would
    // be reflected wholesale into a screen-clearing burst.
    reflectInterval: 0.22,
  },
};

// Harmonics (weapon fusion, see data/fusions.js): two level-8 weapons fuse
// into one hybrid with its own levels 1–maxLevel. Fusing frees a weapon slot
// AND relieves CombatSystem's distinct-name soft-cap — that structural gain is
// ON TOP of the numbers below, which target ≈1.15× the combined level-8
// parents at fusion level 1, rising to ≈1.5× at max.
//
// "≈1.15× the parents" is a measured claim, not an aspiration: the ratio is
// raw single-target output on the bench in
// systems/combat/weapons/fusions/dpsBench.js, asserted per fusion by
// fusionBalance.test.js. Read that file's header before touching any number
// here — the ratios are NOT what the config reads suggest. Damage that
// re-ticks (a lingering zone, an orbiting shard) is worth several times its
// listed number, a piercing shot is worth its `hits`, and a level-gated
// projectile COUNT compounds with per-level damage. Every field below was
// solved against the bench; change one and re-run the test rather than
// eyeballing it.
//
// Each `<field>PerLevel` is the per-level slope of `<field>`, kept here rather
// than as a literal in the weapon class so the whole curve is tunable from one
// place. The slopes are small on the fusions whose capstone is structural
// (Cannonade's every-2nd slug, Static Bloom's extra needles) — those capstones
// already spend most of the L1→max budget.
//
// `iconFallback` names the parent whose HUD ring sprite the fusion borrows
// (see Game._drawWeaponIcons) until fusions get sprites of their own.
export const FUSED_WEAPONS = {
  novaWard: {
    name: 'Nova Ward',
    maxLevel: 5,
    iconFallback: 'Orbit Ward',
    color: '#e0c3ff',
    shardCount: 6, // flat — growth comes from damage/pulses, not ring bloat
    shardDamage: 49,
    shardDamagePerLevel: 2.6,
    orbitRadius: 36,
    angularSpeed: 8,
    hitCooldownDuration: 0.25,
    projectileRadius: 4,
    pulseInterval: 1.6, // every shard blooms a micro-pulse on this shared beat
    pulseRadius: 24,
    pulseDamage: 39,
    pulseDamagePerLevel: 2.1,
    // Max-level flourish: each micro-pulse leaves a brief lingering zone.
    // It re-ticks every 0.25s for 0.9s, so it is worth ~4x its per-tick
    // damage — hence a per-tick scale this small.
    lingerRadiusScale: 0.6,
    lingerDamageScale: 0.1,
  },
  stormLance: {
    name: 'Storm Lance',
    maxLevel: 5,
    iconFallback: 'Spirit Lance',
    color: '#9fd8ff',
    damage: 134,
    damagePerLevel: 7,
    fireRate: 0.7,
    projectileSpeed: 300,
    projectileRadius: 4,
    lifetime: 1.6,
    shedInterval: 0.12, // seconds between needles shed by a lance in flight
    needleDamage: 40,
    needleDamagePerLevel: 2.1,
    needleSpeed: 220,
    needleTurnRate: 4.5,
    needleLifetime: 2.0,
  },
  cannonade: {
    name: 'Cannonade',
    maxLevel: 5,
    iconFallback: 'Scatter Cannon',
    color: '#ffe08a',
    fireRate: 0.85,
    pellets: 7,
    spread: 0.5,
    pelletDamage: 91,
    pelletDamagePerLevel: 1.7,
    projectileSpeed: 240,
    projectileRadius: 2,
    pelletLifetime: 0.8,
    slugEvery: 3, // every Nth volley is the piercing slug instead (2 at max level)
    slugDamage: 273,
    slugDamagePerLevel: 5.1,
    slugSpeed: 320,
    slugRadius: 5,
    slugLifetime: 1.8,
  },
  // --- Act II pairings (ACT2-BIBLE.md §7) ---------------------------------
  shatterpoint: {
    name: 'Shatterpoint',
    maxLevel: 5,
    iconFallback: 'Nova Pulse',
    color: '#d9b8ff',
    radius: 78,
    pulseDamage: 279,
    pulseDamagePerLevel: 9.9,
    // The max-level aftershock that fills the gap. Kept quiet on purpose: it
    // lands every pulse, so a loud echo would be worth more than the entire
    // L1->max damage budget on its own.
    echoRadiusScale: 0.6,
    echoDamageScale: 0.15,
    // A long gap on purpose — the Shatterpoint should read as an event you can
    // position for rather than a continuous hum.
    fireRate: 1.2,
  },
  halo: {
    name: 'Halo',
    maxLevel: 5,
    iconFallback: 'Orbit Ward',
    color: '#ffeec2',
    shardCount: 5,
    shardDamage: 120,
    shardDamagePerLevel: 9.9,
    orbitRadius: 30,
    angularSpeed: 7,
    hitCooldownDuration: 0.25,
    projectileRadius: 4,
    // Orbit Ward's shards gave up bullet-blocking; the Halo buys it back —
    // the fusion's structural payoff, alongside the light it carries.
    blocksBullets: true,
    lightRadius: 74,
  },
  lumenLance: {
    name: 'Lumen Lance',
    maxLevel: 5,
    iconFallback: 'Spirit Lance',
    color: '#ffd98a',
    damage: 229,
    damagePerLevel: 18.9,
    fireRate: 0.55,
    projectileSpeed: 320,
    projectileRadius: 4,
    lifetime: 1.6,
    pierce: 4,
    // Keeps the lantern's light around its wielder — smaller than the
    // Lantern's own, since the lance trades closeness for reach.
    lightRadius: 52,
  },
  // --- Act III (ACT3-BIBLE.md §7) -------------------------------------------
  // Act III's two weapons shipped as champion starters only, which left them
  // out of the fusion table entirely — the one pair in the game that could
  // never fuse. One recipe each, on Act II's pattern: each pairs the new
  // weapon with an existing one, so it multiplies the table rather than
  // sitting beside it.
  rubric: {
    name: 'Rubric',
    maxLevel: 5,
    iconFallback: 'Nova Pulse',
    color: '#ffb347',
    fireRate: 0.85,
    marks: 3, // flat — see Rubric.markCount
    range: 58,
    projectileRadius: 15,
    // The fusion's whole payoff: a mark burns instead of expiring on one hit,
    // so `damage` below is a per-tick number worth several times its face over
    // the mark's dwell. Solved on the bench; see the header above.
    lifetime: 2.2,
    hitCooldownDuration: 0.3,
    damage: 9.1,
    damagePerLevel: 0.75,
  },
  antiphon: {
    name: 'Antiphon',
    maxLevel: 5,
    iconFallback: 'Arc Needle',
    color: '#7ce8d0',
    fireRate: 0.42,
    // Arc/reflect fields are the Countersong's contract, read by
    // Game._convertBossBullets through the inherited accessors.
    arcRadius: 54,
    arcWidth: 1.7,
    reflectDamage: 74,
    reflectDamagePerLevel: 9.5,
    reflectSpeed: 340,
    reflectInterval: 0.16, // tighter than the Countersong's 0.22 — the response keeps up
    needles: 3, // flat — see Antiphon.needleCount
    needleTurnRate: 4.5,
    projectileSpeed: 250,
    projectileRadius: 2.5,
    lifetime: 2.2,
    damage: 40,
    damagePerLevel: 3.4,
  },
  staticBloom: {
    name: 'Static Bloom',
    maxLevel: 5,
    iconFallback: 'Nova Pulse',
    color: '#c8f0ff',
    fireRate: 1.5,
    radius: 85,
    pulseDamage: 262,
    pulseDamagePerLevel: 1.7,
    needleDamage: 31.5,
    needleDamagePerLevel: 0.2,
    needleSpeed: 240,
    needleTurnRate: 5,
    needleLifetime: 2.0,
  },
};

// Ultimate meter (see systems/combat/UltimateSystem.js). Every champion has
// one signature ultimate (data/ultimates.js), charged on a constant clock and
// nudged along by grazes. Since the Awakening merge this is the game's only
// charged meter and its only charged button (Space) — firing it also grants
// the generic combat surge Awakening used to own. Damage dealt deliberately
// does NOT charge the bar: the cycle should read the same to the player in
// every fight, so the ultimate arrives on a predictable beat instead of
// tracking DPS, gear and whatever HP pool happens to be on the field.
export const ULTIMATE = {
  // The whole economy: the trickle alone fills an empty bar in
  // `1 / passiveChargePerSecond` seconds (50s), so the ultimate comes back on
  // a fixed cadence regardless of what's being fought.
  passiveChargePerSecond: 1 / 50,
  // The one skill input left on the meter — daring play shortens the wait a
  // little without making the cycle unpredictable (~83 grazes for a full bar
  // on grazes alone, so a graze is worth roughly 0.6s off the clock).
  chargePerGraze: 0.012,
  // BULLET-HELL.md §D7 — an absorbed bullet (matched attunement, see POLARITY)
  // pays this, scaled by the resonance chain. Priced above a graze because it
  // costs more: a graze asks you to pass near a bullet, an absorb asks you to
  // stand in one and be RIGHT about which tone it is. Only Act II's toned
  // patterns can pay it at all, so it cannot bend the cadence of any other
  // fight.
  chargePerAbsorb: 0.015,
  // Runs open on an empty bar, so the very first ultimate lands on the same
  // 50s beat as every one after it. (This was a 0.25 head start back when the
  // bar took 100s to fill and a new player might not see the button at all in
  // their first stage; at half that wait the head start only bought an
  // out-of-rhythm opener.)
  startingCharge: 0,
};

export const KEYBINDS = {
  up: ['KeyW', 'ArrowUp'],
  down: ['KeyS', 'ArrowDown'],
  left: ['KeyA', 'ArrowLeft'],
  right: ['KeyD', 'ArrowRight'],
  dash: ['ShiftLeft', 'ShiftRight'],
  // Focus mode (§D4). The genre's home for this is Shift, which dash already
  // owns here and has since the game shipped — C is the next-closest thing to
  // a free key that a left hand on WASD can hold indefinitely. Deliberately
  // NOT Ctrl: the browser turns Ctrl+W and friends into tab commands, and a
  // focus key that can close the tab mid-pattern is worse than an awkward one.
  focus: ['KeyC'],
  pause: ['Escape', 'KeyP'],
  // Space is the ultimate's home since the Awakening merge; Q/E stay bound
  // as aliases for players used to them.
  ultimate: ['Space', 'KeyQ', 'KeyE'],
  // The champion key. Act III's Fugue swaps between her two stances
  // (data/characters.js's Countersubject); Tine flips her attunement with it
  // (Attunement, BULLET-HELL.md §D7). One binding rather than two because no
  // champion has both verbs, and it stays inert for every champion that has
  // neither, so it costs the rest of the roster nothing.
  stanceSwap: ['KeyF'],
  // Index-matched to the level-up pick screen's up-to-3 cards (see
  // LevelUpPicker.js) — Digit1 picks card 0, Digit2 picks card 1, etc.
  // 4 entries because Lucky Star (Glimmer pet unique, data/pets.js) can
  // put a 4th card on the pick screen; without it, key 4 has no card.
  levelUpPick: ['Digit1', 'Digit2', 'Digit3', 'Digit4'],
};

// XP Motes (BULLET-HEAVEN.md §H1) — the kill -> gem -> level microloop.
//
// Minions no longer pay XP the instant they die. They drop a physical object
// you have to move over, and the XP the kill was worth (plus everything the
// whittling-down accrued) is banked on it until you do. The point is not the
// XP, which is unchanged in total; it is that the survival verb and the reward
// verb become the same stick. You can also SEE how the last ten seconds went,
// scattered on the floor, which an XP bar cannot show you.
//
// Nothing here makes a run richer — `progressionPacing.test.js` holds the
// seconds-per-card bands either way. It changes where the XP is, not how much.
export const MOTES = {
  radius: 2.2, // small; a wave's worth on the floor has to read as confetti, not clutter
  // Collection radius before any `magnetRadius` stat. Deliberately larger
  // than body contact would give: brushing past a mote should take it, since
  // the whole loop dies if collecting requires precision at the same moment
  // dodging does.
  magnetBase: 14,
  magnetSpeed: 190, // px/sec a mote inside the magnet radius travels toward the player
  // How long a mote waits before it gives up and streams in on its own.
  // The escape valve: a cautious player never loses progression permanently.
  // The arena is 700x500 and camera-clamped, so hoarding isn't the exploit it
  // would be in an open field — this is about not punishing kiting, not about
  // stopping an exploit.
  lifetime: 12,
  vacuumSpeed: 520, // px/sec once a mote is vacuuming — fast enough to read as a stream
  // Motes older than this, and every mote on the field at a stage clear,
  // vacuum at once. Late-run this is the genre's signature image: hundreds of
  // gems pouring in from offscreen, firing back-to-back level-ups.
  maxActive: 600, // hard cap; oldest vacuum early rather than the field growing without bound
  // Tiers are legibility, not economy: the XP a mote carries is whatever its
  // source banked (see ProgressionSystem), and the tier only picks the size
  // and colour so rarity reads at a glance. Half the reward is knowing what
  // you just picked up.
  // One hue family, three brightnesses. **Lime-green is the reward colour and
  // nothing else in the arena uses it** — that exclusivity is the point, and
  // it is enforced by moteLegibility.test.js.
  //
  // The previous palette (#8ad1ff / #c9a8ff / #ffd166) failed the only test
  // that matters for a pickup: `large` was byte-for-byte the Trapper's body
  // colour, and `small`/`medium` sat within a dozen perceptual units of the
  // Anglerling and the Act II casters. So the floor of a wave stage showed
  // blue dots that were rewards next to blue dots that were threats, in a
  // genre whose core skill is parsing a crowded frame at a glance. Tiering by
  // "size and colour so rarity reads" is worth nothing if the colours are the
  // enemy's colours.
  //
  // Tier still reads at a glance, but from *brightness and scale* within one
  // family rather than from three unrelated hues — so learning "lime sparkle
  // = XP" is one rule instead of three, and a bigger, whiter one is
  // self-evidently worth more.
  // Green is the widest unoccupied band in the creature palette — 130 colours
  // across every minion, boss and champion sprite, and none of them live
  // here. These three sit 41 / 39 / 31 perceptual units from the nearest
  // creature colour, against the 0 / 12 / 13 the old palette managed.
  tiers: {
    small: { scale: 1, color: '#24d024' }, // ordinary minion
    medium: { scale: 1.5, color: '#5ef53a' }, // splitters, wardens, the deep — the tougher vocabulary
    large: { scale: 2.2, color: '#c0ff5c' }, // a boss phase break
  },
  // Every mote is drawn as a four-point sparkle with a white core and an
  // additive halo (see Game.js's _drawMotes). No creature in the game has
  // either: minions are matte silhouettes with a dark outline, drawn from
  // buildSilhouette/finishCreature. That contrast — emissive star vs. solid
  // body — is the primary discriminator, and it works at 2px where hue alone
  // does not. Colour separation above is the backstop, not the mechanism.
  sparkleSpread: 0.42, // half-width of a sparkle arm, as a fraction of its length
  haloScale: 2.6, // halo radius as a multiple of the mote radius
  haloAlpha: 0.3,
  // Boss phase transitions burst this many motes instead of the boss paying
  // one lump at death. The phase break becomes a harvest window — a real
  // tactical beat inside a fight, and it costs nothing in difficulty.
  bossPhaseBurst: { min: 8, max: 12, xpEach: 8, spread: 46 },
};

// Wave-stage ramp (BULLET-HEAVEN.md §H2) — the pressure inversion.
//
// The old wave stage was 4 waves x N minions, clear-then-next: a lull, a
// batch, a lull. That reads as a chore before the fight. The ramp replaces it
// with one timed stage on a rising spawn curve — you open against a handful
// and end against the stage's full weight, with no gap anywhere in it. The
// gauntlet then reads heaven -> hell -> heaven -> hell rather than chore ->
// fight, and the boss is what stops the snowball going stale, which is the
// problem pure bullet heavens have to solve with artificial reapers.
//
// Difficulty is deliberately NOT raised — `crowdMultiplier` below raises the
// BODY COUNT and softens each body in the same step, so a stage's total
// pressure is held roughly constant while its density triples. What changes is
// the shape and the texture; what does not change is how hard the stage hits.
// Both halves of that trade are asserted in MinionWaveSystem.test.js.
export const WAVE_RAMP = {
  // `waveConfig.waveCount` is reinterpreted as duration in units of this many
  // seconds, so each boss's existing per-stage tuning carries over meaningfully
  // instead of needing a second set of numbers: the 4-wave stages become ~100s.
  secondsPerWaveUnit: 25,
  // Concurrent enemies as a fraction of the peak (`minionsPerWave`), from
  // stage start to stage end. 0.35 of a peak-8 stage opens on 3 bodies.
  concurrentStart: 0.35,
  // Curve shape for that rise. Above 1 means the stage spends longer in its
  // early, readable half and steepens late — the snowball has to visibly
  // ARRIVE before the stage ends or the whole mechanism fails.
  concurrentExponent: 1.6,
  // Fractions of stage time at which the harder minion vocabulary is
  // introduced (see MinionWaveSystem's TYPE_TIERS). The ramp is qualitative as
  // well as numeric: more enemies AND worse ones, rather than just more.
  midTypesAt: 0.4,
  lateTypesAt: 0.7,
  // Minimum gap between spawn ticks. Spawning is otherwise driven purely by
  // "is the field below its target size", which at high kill rates would
  // replace bodies the same frame they died and read as enemies teleporting
  // into the player's face.
  spawnInterval: 0.45,
  // Most bodies one spawn tick may add. Keeps a sudden clear (a Nova Pulse
  // wiping the field) from re-filling it in a single instant.
  maxSpawnBurst: 3,

  // --- crowd density ------------------------------------------------------
  //
  // A wave stage's `minionsPerWave` (8-14 across the gauntlet) was authored as
  // a PRESSURE budget, and as a density it is far too thin to read as a bullet
  // heaven. The reason is geometry that lives nowhere in the wave numbers: the
  // arena is 700x500 but the viewport is 384x216, so the player only ever sees
  // ~24% of the field. A peak of 14 bodies renders as roughly three on screen.
  //
  // This multiplies peak concurrency, and `MinionWaveSystem._crowdSoftening`
  // divides per-body stats in the same step so the trade is explicit and
  // reversible from this one number:
  //   - HP is divided by the full multiplier, so the stage's total effective
  //     HP — and therefore its time-to-clear and its mote income — is
  //     unchanged. More bodies, each dying faster.
  //   - Damage is divided by its square root, not the full multiplier. Contact
  //     damage is already rate-limited by the player's 0.4s mercy window, so
  //     worst-case incoming DPS does not scale with body count the way total
  //     HP does; dividing by the full multiplier would make a crowd strictly
  //     safer than the batch it replaced. The square root leaves a crowd
  //     modestly gentler per touch, which is the right direction when the
  //     number of things that can touch you has nearly tripled.
  //
  // **2.8 -> 5.0 (2026-07-27).** §H8 raised this to 2.8 and measured the Tide,
  // which authors its own numbers (`crowdScale: false`) and was fine. Nobody
  // measured the gauntlet's own wave stages, and they were the thin ones —
  // `densityBench.js` exists now so this cannot go unmeasured again:
  //
  //                              on screen    frame covered
  //   Act I opening wave stage       19            2.6%
  //   Act I final wave stage         34            4.6%
  //   Undersong Tide @ 20 min       123           16.7%
  //
  // The gauntlet's densest moment was **less than a third** of the Tide's, so
  // the heaven half of a gauntlet run barely resembled the mode built to
  // demonstrate it. At 5.0 the King's wave stage runs ~62 bodies and ~8.3%,
  // roughly half the Tide — the right relationship for an interlude between
  // boss fights rather than the main event.
  //
  // Honest about the ceiling: 30-50% coverage, the figure BULLET-HEAVEN.md
  // quotes for the genre, is not reachable by body count here. A minion is
  // r~5 in a 384x216 frame, roughly half the relative size of a Vampire
  // Survivors enemy, so matching that coverage needs ~4x the bodies rather
  // than 2x. That is a sprite-scale decision, not a spawn-tuning one, and it
  // is deliberately not made here.
  crowdMultiplier: 5,

  // How many times over a ramp stage may replace its field. The slot budget is
  // `waveCount * peakConcurrent * this`, and it is what decides whether a stage
  // reaches its own clock with the field still full or drains partway through.
  //
  // 1 was the old volume-parity figure and it starves: measured against Act I's
  // stages at 25-80 DPS, a stage spent 20-60% of its clock unable to refill. 4
  // holds every one of those cases at zero starvation with margin, and still
  // leaves the budget finite, so a build that genuinely outruns it can end the
  // stage early instead of waiting out the timer.
  budgetTurnover: 4,

  // --- spawn placement ----------------------------------------------------
  //
  // Spawning happens on a ring around the PLAYER rather than at the arena's
  // border (which is what `_randomEdgePosition` did, and still does as the
  // fallback). At the arena's edge a new body is typically 200-400 units from
  // the player and walks at 64-100 px/s, so it spends 3-6 seconds offscreen
  // before it is part of the fight — while `concurrentTarget` has already
  // counted it as satisfied and stopped filling. That is the single largest
  // reason the field reads as empty at any given concurrency.
  //
  // The ring sits just outside the view's corner (half-diagonal of the 384x216
  // viewport, ~220), so a body is never placed in sight but arrives in about a
  // second.
  spawnRingMargin: 26, // world units past the view corner
  spawnRingJitter: 44, // extra depth, so arrivals aren't all on one circle
  // Slack on the "is this offscreen" test, covering the camera's follow lag
  // (FOLLOW_LERP_SPEED 6/s against a 72 px/s player is ~12 units of trail).
  spawnViewMargin: 16,
  // Ring placement is rejection-sampled against the arena rect and the view
  // rect. A player pinned in a corner can have most of the ring outside the
  // arena; after this many misses, fall back to an arena-edge spawn.
  spawnRingAttempts: 12,
};

// Resonant Pillars (destructible cover, see systems/CoverSystem.js): crystal
// wards spawned for boss fights that eat pooled boss/minion bullets until
// they shatter. Blocks BULLETS ONLY — beams, damage zones, and charges pass
// straight through (that asymmetry is the counterplay to camping), and the
// pillars are not solid to movement.
export const COVER = {
  maxActive: 3,
  radius: 10,
  hp: 120, // each blocked bullet deals its damage to the pillar
  minDistPlayer: 100, // never spawns as a freebie shield right on the player…
  minDistBoss: 100, // …or point-blank on the boss
  spawnXFractions: [0.3, 0.7], // the opening mirrored pair
  placementJitter: 30, // px of randomness so repeat fights don't feel stamped
};

// Fixed-timestep simulation rate — decouples game logic from display refresh
// rate so dash timing/i-frames stay consistent across monitors.
export const SIM_HZ = 60;
export const SIM_DT = 1 / SIM_HZ;

// How long a boss's desperation finale (triggered at 0 HP) lasts. The boss is
// invulnerable and frozen the whole time while an escalating barrage plays —
// the player just has to survive it (see SpawningSystem._updateDesperation).
export const DESPERATION_DURATION = 20; // seconds (was 30)

// Spell-card capture (BULLET-HELL.md §D1). Every boss attack pattern is a
// named card: survive it start-to-finish without taking a hit and it is
// CAPTURED. The game already owned 66 named, telegraphed, distinct patterns
// and never told the player that dodging one cleanly was an achievement —
// this is the payout that says so.
//
// Payout scales with how dangerous the card actually was, measured live as
// `telegraphDuration × bullets it fired` rather than from a hand-authored
// table: a per-name table would need a row for all 66 (and every future
// pattern), and would go stale the moment a boss's ATTACK_CONFIG row changed
// the bullet counts. The measured version follows tuning for free, and it
// prices the same pattern higher on a later boss that fires more of it.
export const CAPTURE = {
  // Danger score that pays exactly one "unit" of the per-unit terms below.
  // ~30 = a 1.2s telegraph into 25 bullets, i.e. a solid mid-fight card;
  // SovereignsWrath-class cards land at several units, RadialRing under one.
  dangerPerUnit: 30,
  unitsMax: 4, // ceiling on the scaling so a layered mega-card can't pay out absurdly
  // Glimmer (vanity currency) — deliberately the most generous term, since
  // it buys nothing that touches combat. Banked per capture, paid at run end
  // alongside the graze payout (Currency.calcGlimmer).
  glimmerBase: 2,
  glimmerPerUnit: 3,
  // Ultimate charge, in bar fractions. A clean card is worth noticeably more
  // than the grazes taken to thread it (ULTIMATE.chargePerGraze = 0.012), so
  // capture becomes the fastest honest way to charge the meter.
  chargeBase: 0.04,
  chargePerUnit: 0.05,
  // XP, as a mote burst at the boss (the harvest window BULLET-HEAVEN.md's H1
  // wants, on the boss half of the run). Count and value both scale; the
  // total is small next to a boss kill's 250 so captures season the level
  // curve rather than driving it.
  moteCountBase: 3,
  moteCountPerUnit: 2,
  moteCountMax: 10,
  moteXpBase: 10,
  moteXpPerUnit: 14,
  // Grazes taken during the card raise its whole payout — risk inside a clean
  // dodge is the most skilful thing a player can do in this game, and capture
  // is the only place it can be priced without unbalancing the meter.
  grazeBonusPerGraze: 0.02,
  grazeBonusMax: 0.6,
  // The desperation window is the boss's last word: one card, one attempt,
  // no picker, twenty seconds. Worth a multiple.
  desperationMultiplier: 3,
  bannerDuration: 1.7, // seconds the CAPTURED banner stays on screen
  // A cold cyan-white, deliberately unlike every existing feedback hue — no
  // boss bullet, telegraph, mote or pickup uses it, so "card captured" can
  // never be mistaken for a hit landing or a threat appearing. Shared by the
  // banner (Game._drawCaptureBanner) and the spark burst (JuiceDirector).
  color: '#8ef0ff',
};

// Bullet cancel (BULLET-HELL.md §D2).
//
// The genre's universal reward-for-aggression rule: cross a boss phase
// threshold — or fire the ultimate — and every live bullet on screen is
// deleted and converted into XP motes. It does three jobs with one mechanic.
// It prices damage (breaking a phase inside a denser pattern converts more
// bullets, so aggression pays without a single damage number moving); it is a
// readability release valve at exactly the moment the screen is fullest and a
// new pattern set is about to be introduced; and it turns the ultimate into a
// real danmaku bomb — defensive AND rewarding — which it was not before.
//
// It replaces MOTES.bossPhaseBurst's fixed ring as the phase-break payout,
// keeping that ring only as a floor (see Game's boss:phase-change handler): a
// payout proportional to the danger you were actually standing in is the whole
// point, but a phase break that happens to land on an empty board should still
// pay the beat the player has learned to expect.
export const CANCEL = {
  // XP for a bullet of `referenceRadius`. Sized against MOTES.bossPhaseBurst's
  // 8 xp so a *typical* phase break (20-40 bullets in the air) lands in the
  // same order of magnitude as the fixed burst it replaces, and a break inside
  // a GridBarrage pays several times it.
  xpPerBullet: 6,
  referenceRadius: 3,
  // Fat bullets are worth more, but sublinearly and clamped, so cancelling a
  // dense wall of small bullets and cancelling a handful of large ones pay
  // comparably — the reward is for the danger, and a 60-bullet curtain is more
  // dangerous than six boulders however the pixels add up.
  radiusScaleMin: 0.7,
  radiusScaleMax: 2.2,
  // Mote tier is legibility only (MOTES.tiers) — it picks size and colour.
  mediumRadius: 5,
  largeRadius: 9,
  // Hard cap on motes produced, so a GridBarrage break doesn't put 200 pickups
  // on the floor. Every bullet is still cancelled; the cancelled bullets' XP is
  // pooled and paid across this many motes instead of being lost.
  maxMotes: 40,
};

// Deathbomb (BULLET-HELL.md §D3).
//
// A hit that lands while the ultimate meter is full does not resolve
// immediately: it hangs in the air for `window` seconds, and firing the
// ultimate inside that window voids it entirely. This is the genre's most
// humane mechanic — it converts "that was unfair" into "I was 100ms late",
// which is the difference between a player retrying and a player quitting.
// It is never free (it costs a full bar, ~50s of fill) and it rewards
// reaction rather than granting immunity.
//
// The deferral only happens when the bar is actually full, so a run that
// can't bomb resolves damage exactly as it always did — nothing about hit
// timing changes for a player who never charges the meter.
export const DEATHBOMB = {
  // Sim-seconds. Stretched in real time by the slow-mo below, so what the
  // player actually gets to react in is roughly 0.4s of wall clock.
  window: 0.16,
  // reduceEffects damps slow-mo to a quarter of its depth (JuiceDirector),
  // which would leave the window at ~0.19s real and make the mechanic
  // unusable for exactly the players the setting exists for. Give that
  // branch the real time back directly instead.
  windowReduced: 0.34,
  slowMoScale: 0.3,
  slowMoDuration: 0.45, // real seconds — see JuiceDirector.slowMo
  // The ultimate meter's gold: the cue names the resource being spent, and
  // reuses a hue the player already reads as "the bar is full".
  color: '#ffd166',
};

// Suicide bullets (BULLET-HELL.md §D6).
//
// A dying minion fires a small aimed fan at the player. Cave's answer to the
// problem BULLET-HEAVEN.md §H2 is also chasing: pressure that scales with
// YOUR clear rate rather than with a clock. The faster you mow, the more
// bullets you author — so it self-balances across build strength without
// touching a single enemy stat, it makes the wave half require dodging (which
// today it barely does), and it gives D2's bullet-cancel something to cancel
// outside a boss fight.
//
// Gated to Act II+ (MinionWaveSystem reads `waveConfig.suicideBullets`, which
// Game._waveConfig sets per act) so Act I's ramp is untouched, and tuned
// conservatively in all three of the axes that matter: slow, aimed, sparse.
export const SUICIDE = {
  // Fraction of deaths that fire at all. Under half on purpose: the mechanic
  // has to read as a punctuation mark on a kill, not as a property of killing.
  // With 3-5 bullets a burst that is ~1.4 bullets per body — one Act II wave
  // stage's worth of clears then authors a few hundred over several minutes,
  // which is texture, not a curtain.
  chance: 0.35,
  minBullets: 3,
  maxBullets: 5,
  // Radians of the fan, centred on the player. Wide enough that standing still
  // is punished and narrow enough that one step sideways answers it — the
  // bullets are the reason to keep moving between kills, not a tax on killing.
  spread: 0.9,
  // Slower and weaker than the same minion's aimed shot while alive. A burst
  // that outran the living creature's own fire would be strictly better to
  // trigger late, which would make killing things feel bad.
  speedScale: 0.55,
  damageScale: 0.7,
  radius: 2.5,
  // Short enough that a burst is a local hazard you walk out of rather than
  // something that finds you two rooms later.
  lifetime: 2.6,
  // Its own hue, not the minion orange (#e8873a): a bullet whose rule is
  // "something died here" has to be distinguishable at a glance from one whose
  // rule is "something is shooting at you", because the answers differ — you
  // step off the corpse, you don't look for the shooter.
  color: '#c98cd8',
  // Rate limit, in bursts per second, with `burstCap` bankable. A field wipe
  // (an ultimate, a bullet-cancel into an AoE build) kills 30 bodies in one
  // frame, and unlimited bursts would answer the player's biggest moment by
  // instantly refilling the screen with the bullets they just cleared. The
  // limit means clear RATE is what authors bullets — which is the mechanic —
  // while a single burst of overkill is still allowed to feel clean.
  burstsPerSecond: 3,
  burstCap: 4,
  // Deaths further than this from the player fire nothing (px). Derived from
  // the view rather than tuned: a body dying offscreen would author bullets
  // that arrive with no tell at all, which is the one way this mechanic could
  // be unfair. Half the viewport diagonal is the farthest anything on screen
  // can be, so anything possibly visible still fires.
  maxRange: Math.hypot(VIRTUAL_WIDTH / 2, VIRTUAL_HEIGHT / 2),
  // Types that already say something on death, and must not say two things at
  // once: the Bomber detonates, the Splitter becomes two children, the
  // Swarmling exists to be deleted six at a time (a cluster's worth of bursts
  // would be a wall), and the Tollkeeper's entire design is that it cannot
  // hurt you — see MinionWaveSystem._createMinion for each.
  exemptTypes: new Set(['bomber', 'splitter', 'swarmling', 'tollkeeper']),
  // Attributed as its own hazard in the death recap (see data/damageSources),
  // not as the minion that fired it: "a Skirmisher killed me" would be a lie
  // about the mechanic when the Skirmisher was already dead.
  source: 'deathBurst',
};

// Polarity payoff (BULLET-HELL.md §D7) — "resonance".
//
// Act II's tone rule (SpawningSystem._checkPlayerCollisions) already lets a
// bullet matching the player's attunement wash through harmlessly. That is
// Ikaruga's mechanic with its economy removed: in Ikaruga, absorbing is
// *income* — it charges a release and chains — which is the whole reason the
// player swims INTO matching fire instead of merely tolerating it. Without the
// income, flipping tone is a defensive chore.
//
// So an absorbed bullet now pays: ultimate charge (ULTIMATE.chargePerAbsorb)
// scaled by a resonance chain, and every `stacksPerRelease` absorbs discharge
// the stored fire back at whatever is nearest as a homing volley. Both halves
// are deliberately only reachable through toned bullets, so nothing outside
// Act II's polarity fights changes.
export const POLARITY = {
  // Seconds a resonance chain survives without a new absorb. Longer than
  // GRAZE.chainWindow because the thing being measured is coarser: grazes come
  // bullet by bullet inside one card, absorbs come ring by ring as TwinVoice
  // volleys arrive, and a window shorter than the gap between rings would make
  // the chain unbuildable no matter how well the player read the tones.
  window: 2.5,
  // Multiplier = 1 + stacks * perStack, capped. The cap lands at 20 stacks —
  // most of two full TwinVoice rings absorbed cleanly, which is a real read
  // rather than something incidental contact reaches.
  multiplierPerStack: 0.05,
  multiplierMax: 2,
  // Absorbs per release. 12 is under one Twin Voice ring's matched half (14),
  // so a player who reads a whole ring correctly gets a release from it —
  // the payoff has to be attached to the skill it is teaching, not to a
  // longer-term budget.
  stacksPerRelease: 12,
  // The release: homing shots at whatever is nearest, fanned so they arc in
  // rather than arriving as one line.
  releaseShots: 6,
  releaseSpread: 1.6, // radians of the launch fan
  // Per-shot damage BEFORE the build's damage multiplier (applied at fire time
  // so a release stays worth casting in a strong build). Sized at roughly a
  // second of a mid-run build's output for a full 12-absorb chain: a reward
  // for a read, not a damage source you can build around.
  releaseDamage: 55,
  releaseSpeed: 210,
  releaseTurnRate: 4.5,
  releaseLifetime: 1.8,
  // Violet — the tone-neutral colour between the Antiphon's warm (#ff8a4d) and
  // cold (#4dc9ff), because a release is what absorbing EITHER tone buys. The
  // readout and the volley share it so the meter visibly becomes the shots.
  // Pushed toward magenta to stay distinct from Aria's cascade violet
  // (#c9a0ff, JuiceDirector's REFRAIN_COLOR), which can be on screen at once.
  color: '#d98cff',
  // Seconds the readout lingers after a chain lapses (matches GRAZE.readoutFade).
  readoutFade: 0.5,
};
